import { useState } from 'react';
import { Trash2, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@lean-agility/design-system/react';
import { Button } from '@lean-agility/design-system/react';
import { Dialog } from '@/components/dialog/Dialog';
import { DialogActions } from '@/components/dialog/DialogActions';
import { LicenseBadge } from '../atoms/LicenseBadge';
import { useLicenseDetails, useDeleteLicense } from '../../hooks';
import { useErrorHandler } from '@/hooks/useErrorHandler';

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      className="
        flex justify-between border-b border-border-default/30 py-2
        last:border-0
      "
    >
      <span className="text-sm text-text-secondary">{label}</span>
      <span className="text-sm font-medium text-text-primary">{value ?? '—'}</span>
    </div>
  );
}

export function LicenseStatusCard() {
  const { data: details, isLoading } = useLicenseDetails();
  const { mutateAsync: removeLicense, isPending: isDeleting } = useDeleteLicense();
  const { handleSuccess, handleError } = useErrorHandler('License');
  const [showConfirm, setShowConfirm] = useState(false);

  const handleRemove = async () => {
    try {
      await removeLicense();
      handleSuccess('License removed');
      setShowConfirm(false);
    } catch (err) {
      handleError(err);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Current License</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-6 animate-spin text-text-muted" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!details || details.status === 'none') {
    return (
      <Card data-testid="license-status-card">
        <CardHeader>
          <CardTitle>Current License</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="py-4 text-sm text-text-muted" data-testid="no-license-message">
            No license installed.
          </p>
          {details?.deployment_id && (
            <DetailRow
              label="Deployment ID"
              value={<span className="font-mono text-xs">{details.deployment_id}</span>}
            />
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="license-status-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Current License</CardTitle>
            <LicenseBadge status={details.status} />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-0">
            {details.customer_name && <DetailRow label="Customer" value={details.customer_name} />}
            {details.customer_id && <DetailRow label="Customer ID" value={details.customer_id} />}
            {details.license_id && (
              <DetailRow label="License ID" value={<span className="font-mono text-xs">{details.license_id}</span>} />
            )}
            {details.issued_at && <DetailRow label="Issued" value={details.issued_at.slice(0, 10)} />}
            <DetailRow
              label="Expires"
              value={details.expires_at ? details.expires_at.slice(0, 10) : 'Never (perpetual)'}
            />
            {details.days_remaining != null && <DetailRow label="Days remaining" value={details.days_remaining} />}
            {details.max_users != null && <DetailRow label="Max users" value={details.max_users} />}
            {details.max_simulations != null && <DetailRow label="Max simulations" value={details.max_simulations} />}
            {details.allowed_features.length > 0 && (
              <DetailRow label="Features" value={details.allowed_features.join(', ')} />
            )}
            <DetailRow
              label="Deployment ID"
              value={<span className="font-mono text-xs">{details.deployment_id}</span>}
            />
            {details.license_deployment_id && (
              <DetailRow
                label="Licensed deployment"
                value={<span className="font-mono text-xs">{details.license_deployment_id}</span>}
              />
            )}
          </div>
          <div className="pt-4">
            <Button
              variant="ghost-danger"
              size="sm"
              onClick={() => setShowConfirm(true)}
              data-testid="remove-license-button"
            >
              <Trash2 className="size-4" />
              Remove License
            </Button>
          </div>
        </CardContent>
      </Card>

      {showConfirm && (
        <Dialog
          title="Remove License"
          onClose={() => setShowConfirm(false)}
          dismissible={!isDeleting}
          maxWidth="max-w-sm"
        >
          <p className="text-sm text-text-secondary">
            This will remove the current license. Gated features will become unavailable until a new license is
            installed.
          </p>
          <DialogActions>
            <Button variant="secondary" size="sm" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleRemove}
              disabled={isDeleting}
              data-testid="confirm-remove-license"
            >
              {isDeleting && <Loader2 className="size-4 animate-spin" />}
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
